import React, { useMemo, useState, useEffect, useCallback } from 'react';
import { Spin, Row, Col, Alert } from 'antd';
import { useSearchParams } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import type { AppDispatch, RootState } from '../../store';
import {
  workspaceApi,
  useGetWorkspaceQuery,
  WorkspaceApplication,
  WorkspaceResponse,
  type WorkspaceTask,
} from '../../store/api/workspaceApi';
import { PageLayout } from '../../ui';
import { WorkspaceKPI } from '../../components/workspace/WorkspaceKPI';
import { WorkspaceApplicationTasks, WorkspaceRouteTasks } from '../../components/workspace/WorkspaceTasks';
import { WorkspaceMyRoutes, WorkspaceMyApplications } from '../../components/workspace/WorkspaceRoutes';
import { WorkspaceRegistryDrawer } from '../../components/workspace/WorkspaceRegistryDrawer';
import { WorkspaceRegistrySummary } from '../../components/workspace/WorkspaceRegistrySummary';
import ApplicationTaskModalHost from '../../components/applications/ApplicationTaskModalHost';
import { resolveWorkspaceTaskModal } from '../../components/workspace/workspaceTaskModalResolve';
import GovernanceRiskHeader from '../../components/governance/GovernanceRiskHeader';
import { WorkspaceAttentionInbox } from '../../components/workspace/WorkspaceAttentionInbox';
import { WORKSPACE_ATTENTION_INBOX_V1_ENABLED } from '../../domain/workspaceAttentionInbox';
import { DashboardFinanceContextDrawer, type DashboardFinanceDrawerTarget } from '../../components/dashboard/DashboardFinanceContextDrawer';
import { useWorkspaceCollaboration } from '../../collab/useWorkspaceCollaboration';

function normalizeWorkspaceRole(name: string | null | undefined): string {
  return String(name || '').trim().toLowerCase();
}

function workspaceFinanceContextEnabled(roleNorm: string): boolean {
  return (
    roleNorm === 'accountant' ||
    roleNorm === 'finance' ||
    roleNorm === 'admin' ||
    roleNorm === 'manager' ||
    roleNorm === 'director'
  );
}

const WorkspacePage: React.FC = () => {
  const dispatch = useDispatch<AppDispatch>();
  const authUser = useSelector((s: RootState) => s.auth.user);
  const roleNorm = normalizeWorkspaceRole(authUser?.role?.name);
  const financeContextDrawerEnabled = workspaceFinanceContextEnabled(roleNorm);
  const [searchParams, setSearchParams] = useSearchParams();
  const { data, isLoading, refetch } = useGetWorkspaceQuery(undefined);
  const [activeTask, setActiveTask] = useState<WorkspaceTask | null>(null);
  const [financeDrawerTarget, setFinanceDrawerTarget] = useState<DashboardFinanceDrawerTarget>(null);
  const [routesRegistryOpen, setRoutesRegistryOpen] = useState(false);
  const [applicationsRegistryOpen, setApplicationsRegistryOpen] = useState(false);
  const { workspaceUsers, financeUsers } = useWorkspaceCollaboration(authUser?.id);

  /** Только первичная загрузка — refetch задач не скрывает страницу и не сбрасывает scroll */
  const loading = isLoading;
  const workspace = (((data as { data?: WorkspaceResponse } | undefined)?.data ?? data) || undefined) as
    | WorkspaceResponse
    | undefined;

  const overdueTasks = useMemo(() => workspace?.overdueTasks ?? [], [workspace]);
  const myTasks = useMemo(() => workspace?.myTasks ?? [], [workspace]);
  const myRoutes = useMemo(() => workspace?.myRoutes ?? [], [workspace]);
  const myApplications: WorkspaceApplication[] = useMemo(
    () => workspace?.myApplications ?? [],
    [workspace]
  );

  const overdueCount = workspace?.overdueCount ?? overdueTasks.length;
  const taskCount =
    workspace?.taskCount ?? myTasks.filter((t) => ['OPEN', 'OVERDUE'].includes(String(t.status).toUpperCase())).length;
  const routeCount = workspace?.routeCount ?? myRoutes.length;
  const applicationCount = workspace?.applicationCount ?? myApplications.length;

  const allActiveTasks = useMemo(() => {
    const map = new Map<number, (typeof myTasks)[number]>();
    [...overdueTasks, ...myTasks].forEach((task) => {
      map.set(task.id, task);
    });
    return Array.from(map.values());
  }, [overdueTasks, myTasks]);

  const applicationTaskCounts = useMemo(() => {
    const counts: Record<number, number> = {};
    allActiveTasks.forEach((task) => {
      if (task.applicationId == null) return;
      counts[task.applicationId] = (counts[task.applicationId] ?? 0) + 1;
    });
    return counts;
  }, [allActiveTasks]);

  const routeTaskCounts = useMemo(() => {
    const counts: Record<number, number> = {};
    allActiveTasks.forEach((task) => {
      if (task.routeId == null) return;
      counts[task.routeId] = (counts[task.routeId] ?? 0) + 1;
    });
    return counts;
  }, [allActiveTasks]);

  const openWorkspaceTaskModal = (task: WorkspaceTask) => {
    setActiveTask(task);
  };

  const openFinanceContextDrawer = useCallback((p: { kind: 'route' | 'application'; id: number }) => {
    setFinanceDrawerTarget(p);
  }, []);

  const openFinanceContextForRegistry = financeContextDrawerEnabled ? openFinanceContextDrawer : undefined;

  /** Подтягиваем свежие задачи с сервера и обновляем в кэше только блоки задач (маршруты/заявки списком не трогаем). */
  const refreshWorkspaceTasks = useCallback(async () => {
    try {
      const fresh = await dispatch(
        workspaceApi.endpoints.getWorkspace.initiate(undefined, { forceRefetch: true })
      ).unwrap();
      dispatch(
        workspaceApi.util.updateQueryData('getWorkspace', undefined, (draft) => {
          draft.overdueTasks = fresh.overdueTasks;
          draft.myTasks = fresh.myTasks;
          draft.myRoutes = fresh.myRoutes;
          draft.myApplications = fresh.myApplications;
          if (fresh.taskCount !== undefined) draft.taskCount = fresh.taskCount;
          if (fresh.overdueCount !== undefined) draft.overdueCount = fresh.overdueCount;
          if (fresh.routeCount !== undefined) draft.routeCount = fresh.routeCount;
          if (fresh.applicationCount !== undefined) draft.applicationCount = fresh.applicationCount;
        })
      );
    } catch {
      await refetch();
    }
  }, [dispatch, refetch]);

  const handleModalClose = () => {
    setActiveTask(null);
  };

  /** Опциональный deep link: ?applicationTaskId=123 */
  useEffect(() => {
    const raw = searchParams.get('applicationTaskId');
    if (!raw) return;
    const id = Number(raw);
    const strip = () => {
      const next = new URLSearchParams(searchParams);
      next.delete('applicationTaskId');
      setSearchParams(next, { replace: true });
    };
    if (!Number.isFinite(id) || id <= 0) {
      strip();
      return;
    }
    if (allActiveTasks.length === 0) return;
    const found = allActiveTasks.find((t) => t.id === id);
    if (found && resolveWorkspaceTaskModal(found)) {
      setActiveTask(found);
    }
    strip();
  }, [allActiveTasks, searchParams, setSearchParams]);

  const applicationContextById = useMemo(() => {
    const m: Record<number, { basisDate?: string | null }> = {};
    for (const a of myApplications) {
      m[a.id] = { basisDate: a.basisDate ?? null };
    }
    return m;
  }, [myApplications]);

  if (loading) {
    return (
      <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100%' }}>
        <Spin size="large" />
      </div>
    );
  }

  return (
    <PageLayout
      title="Рабочее пространство"
      subtitle="Очереди задач, блокировки маршрутов и действия по приоритету"
    >
      <div style={{ marginBottom: 8 }}>
        {WORKSPACE_ATTENTION_INBOX_V1_ENABLED ? (
          <WorkspaceAttentionInbox
            allTasks={allActiveTasks}
            routes={myRoutes}
            applications={myApplications}
            roleName={authUser?.role?.name}
            onOpenFinanceContext={openFinanceContextDrawer}
            onTaskAction={openWorkspaceTaskModal}
          />
        ) : (
          <GovernanceRiskHeader compact title="🚨 Требует внимания" />
        )}
      </div>
      {(workspaceUsers.length > 0 || financeUsers.length > 0) && (
        <Alert
          type="info"
          showIcon
          style={{ marginBottom: 12 }}
          message="Совместная работа в реальном времени"
          description={[
            workspaceUsers.length > 0
              ? `Активны в workspace: ${workspaceUsers.map((u) => u.displayName).join(', ')}`
              : '',
            financeUsers.length > 0
              ? `Активны во finance: ${financeUsers.map((u) => u.displayName).join(', ')}`
              : ''
          ]
            .filter(Boolean)
            .join(' • ')}
        />
      )}

      <WorkspaceKPI
        overdueCount={overdueCount}
        taskCount={taskCount}
        routeCount={routeCount}
        applicationCount={applicationCount}
      />

      <Row gutter={[12, 12]}>
        <Col xs={24} lg={12}>
          <WorkspaceRouteTasks
            tasks={allActiveTasks}
            applications={myApplications}
            actionQueueLimit={10}
            onTaskAction={openWorkspaceTaskModal}
            onOpenFinanceContext={openFinanceContextDrawer}
            activeActionTaskId={null}
          />
        </Col>
        <Col xs={24} lg={12}>
          <WorkspaceApplicationTasks
            tasks={allActiveTasks}
            applications={myApplications}
            applicationContextById={applicationContextById}
            actionQueueLimit={10}
            onTaskAction={openWorkspaceTaskModal}
            onOpenFinanceContext={openFinanceContextDrawer}
            activeActionTaskId={null}
          />
        </Col>
      </Row>

      <Row gutter={[12, 12]} style={{ marginTop: 12 }}>
        <Col xs={24} lg={12}>
          <WorkspaceRegistrySummary
            title="Маршруты"
            count={routeCount}
            onOpenAll={() => setRoutesRegistryOpen(true)}
          />
        </Col>
        <Col xs={24} lg={12}>
          <WorkspaceRegistrySummary
            title="Заявки"
            count={applicationCount}
            onOpenAll={() => setApplicationsRegistryOpen(true)}
          />
        </Col>
      </Row>

      <WorkspaceRegistryDrawer
        title="Все маршруты"
        open={routesRegistryOpen}
        onClose={() => setRoutesRegistryOpen(false)}
      >
        <WorkspaceMyRoutes
          variant="drawer"
          routes={myRoutes}
          taskCounts={routeTaskCounts}
          allActiveTasks={allActiveTasks}
          onInlineTaskAction={openWorkspaceTaskModal}
          onOpenFinanceContext={openFinanceContextForRegistry}
        />
      </WorkspaceRegistryDrawer>

      <WorkspaceRegistryDrawer
        title="Все заявки"
        open={applicationsRegistryOpen}
        onClose={() => setApplicationsRegistryOpen(false)}
      >
        <WorkspaceMyApplications
          variant="drawer"
          applications={myApplications}
          taskCounts={applicationTaskCounts}
          allActiveTasks={allActiveTasks}
          onInlineTaskAction={openWorkspaceTaskModal}
          onOpenFinanceContext={openFinanceContextForRegistry}
        />
      </WorkspaceRegistryDrawer>

      <ApplicationTaskModalHost
        task={activeTask}
        onClose={handleModalClose}
        onSuccess={refreshWorkspaceTasks}
      />
      <DashboardFinanceContextDrawer
        open={financeDrawerTarget != null}
        target={financeDrawerTarget}
        onClose={() => setFinanceDrawerTarget(null)}
        allTasks={allActiveTasks}
        onInlineTaskAction={openWorkspaceTaskModal}
        onSwitchTarget={(t) => setFinanceDrawerTarget(t)}
      />
    </PageLayout>
  );
};

export default WorkspacePage;
